(() => {
  if (window.__lunaCustomClothingUploadV11Native) return;
  window.__lunaCustomClothingUploadV11Native = true;

  const ROOT_SELECTOR = '[data-custom-car-upload]';
  const ACCELERATED_SELECTOR = '.accelerated-checkout-block, shopify-accelerated-checkout, shopify-buy-it-now-button, .shopify-payment-button, [data-shopify="payment-button"]';
  const wiredForms = new WeakSet();
  let initialiseQueued = false;

  const roots = () => [...document.querySelectorAll(ROOT_SELECTOR)];

  const getFormForRoot = (root) => {
    if (!(root instanceof Element)) return null;
    const formId = root.dataset.productFormId;
    if (!formId) return null;
    const form = document.getElementById(formId);
    return form instanceof HTMLFormElement ? form : null;
  };

  const getRootForForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return null;

    return roots().find((root) => {
      if (form.contains(root) || root.closest('form') === form) return true;
      if (!form.id) return false;
      if (root.dataset.productFormId === form.id) return true;
      return [...root.querySelectorAll('[form]')].some((field) => field.getAttribute('form') === form.id);
    }) || null;
  };

  const syncRegistrationField = (root) => {
    const registrationWrap = root.querySelector('[data-registration-field]');
    const registrationInput = registrationWrap?.querySelector('input');
    const keepPlate = root.querySelector('[data-plate-choice="keep"]')?.checked === true;

    if (!registrationWrap || !registrationInput) return;

    registrationWrap.hidden = !keepPlate;
    registrationInput.disabled = !keepPlate;
    registrationInput.required = keepPlate;
    if (!keepPlate) registrationInput.value = '';
  };

  const updateFilePreview = (input, root) => {
    const preview = [...root.querySelectorAll('[data-preview-for]')].find(
      (element) => element.dataset.previewFor === input.id
    );
    if (!preview) return;

    const previousUrl = preview.dataset.objectUrl;
    if (previousUrl) URL.revokeObjectURL(previousUrl);

    preview.replaceChildren();
    delete preview.dataset.objectUrl;

    const file = input.files?.[0];
    if (!(file instanceof File)) return;

    const status = document.createElement('span');
    status.className = 'custom-car-upload-block__filename';
    status.textContent = file.name;
    preview.append(status);

    if (file.type.startsWith('image/') && !/hei(c|f)$/i.test(file.name)) {
      const objectUrl = URL.createObjectURL(file);
      preview.dataset.objectUrl = objectUrl;

      const image = document.createElement('img');
      image.src = objectUrl;
      image.alt = 'Selected car photo preview';
      image.className = 'custom-car-upload-block__preview-image';
      preview.prepend(image);
    }
  };

  const showError = (root, message) => {
    let error = root.querySelector('[data-clothing-upload-error]');
    if (!error) {
      error = document.createElement('p');
      error.dataset.clothingUploadError = '';
      error.setAttribute('role', 'alert');
      error.style.margin = '0';
      error.style.fontWeight = '700';
      root.append(error);
    }
    error.textContent = message;
    error.hidden = false;
  };

  const clearError = (root) => {
    const error = root.querySelector('[data-clothing-upload-error]');
    if (!error) return;
    error.textContent = '';
    error.hidden = true;
  };

  const blockFile = (input, message) => {
    if (!(input instanceof HTMLInputElement)) return;
    input.setCustomValidity(message);
    input.reportValidity();
  };

  const normaliseFileEntries = (formData, root) => {
    [...root.querySelectorAll('input[type="file"][name]')].forEach((input) => {
      formData.delete(input.name);

      [...(input.files || [])].forEach((file) => {
        if (file instanceof File && file.size > 0) {
          formData.append(input.name, file, file.name);
        }
      });
    });
  };

  const removeAcceleratedCheckout = (form) => {
    const component = form.closest('product-form-component') || form.parentElement;
    component?.querySelectorAll(ACCELERATED_SELECTOR).forEach((element) => element.remove());
  };

  const validateUploadBeforeSubmit = (form, root, event) => {
    clearError(root);

    const requiredPhoto = root.querySelector('input[type="file"][required]');
    const requiredFile = requiredPhoto?.files?.[0];

    if (!(requiredFile instanceof File) || requiredFile.size === 0) {
      event.preventDefault();
      event.stopImmediatePropagation();
      blockFile(requiredPhoto, 'Please select your car photo again before adding to cart.');
      showError(root, 'Your car photo is required before this item can be added to cart.');
      return false;
    }

    requiredPhoto.setCustomValidity('');

    const fileInputs = [...root.querySelectorAll('input[type="file"][name]')];
    const brokenInput = fileInputs.find((input) =>
      input.files?.length && [...input.files].some((file) => !(file instanceof File) || file.size === 0)
    );

    if (brokenInput) {
      event.preventDefault();
      event.stopImmediatePropagation();
      blockFile(brokenInput, 'This photo was not attached correctly. Please select it again.');
      showError(root, 'One of the selected photos was not attached correctly. Please select it again.');
      return false;
    }

    const invalidField = [...root.querySelectorAll('input, select, textarea')]
      .find((field) => !field.disabled && !field.checkValidity());

    if (invalidField) {
      event.preventDefault();
      event.stopImmediatePropagation();
      invalidField.reportValidity();
      return false;
    }

    return true;
  };

  const wireForm = (form) => {
    if (wiredForms.has(form)) return;
    wiredForms.add(form);

    form.enctype = 'multipart/form-data';
    form.method = 'post';

    form.addEventListener('formdata', (event) => {
      const root = getRootForForm(form);
      if (!root) return;
      normaliseFileEntries(event.formData, root);
    });
  };

  const initialise = () => {
    roots().forEach((root) => {
      syncRegistrationField(root);

      const form = getFormForRoot(root);
      if (!form) return;

      wireForm(form);
      removeAcceleratedCheckout(form);
    });
  };

  const queueInitialise = () => {
    if (initialiseQueued) return;
    initialiseQueued = true;
    requestAnimationFrame(() => {
      initialiseQueued = false;
      initialise();
    });
  };

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const root = input.closest(ROOT_SELECTOR);
    if (!root) return;

    if (input.matches('[data-plate-choice]')) {
      syncRegistrationField(root);
    }

    if (input.type === 'file') {
      input.setCustomValidity('');
      clearError(root);
      updateFilePreview(input, root);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const root = getRootForForm(form);
    if (!root) return;

    validateUploadBeforeSubmit(form, root, event);
  }, true);

  document.addEventListener('click', (event) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const accelerated = path.find((node) => node instanceof Element && node.matches?.(ACCELERATED_SELECTOR));
    if (!accelerated) return;

    const component = accelerated.closest?.('product-form-component');
    const form = component?.querySelector('form');
    const root = getRootForForm(form);
    if (!root) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    showError(root, 'Please use Add to cart so your uploaded car photo is attached correctly.');
  }, true);

  const observer = new MutationObserver(queueInitialise);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialise();
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }, { once: true });
  } else {
    initialise();
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', queueInitialise);
})();